import { useApi } from '@/hooks/useApi';
import {
  Wrench,
  Plus,
  Clock,
  CheckCircle,
  DollarSign,
  AlertCircle,
  Loader2,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────────

type WOStatus = 'draft' | 'approved' | 'assigned' | 'in_progress' | 'completed' | 'invoiced' | 'cancelled';
type WOPriority = 'low' | 'medium' | 'high' | 'urgent';

interface WorkOrder {
  id: string;
  work_order_number: string;
  vehicle_name: string;
  status: WOStatus;
  priority: WOPriority;
  description: string;
  assigned_to_name?: string;
  actual_cost?: number;
  estimated_cost?: number;
  created_at: string;
  completed_at?: string;
}

interface WorkOrdersResponse {
  data: WorkOrder[];
  total: number;
}

interface WorkOrderStats {
  status_counts: Record<string, number>;
  avg_completion_days: number;
  total_cost_this_month: number;
  total: number;
}

const EMPTY_STATS: WorkOrderStats = {
  status_counts: {},
  avg_completion_days: 0,
  total_cost_this_month: 0,
  total: 0,
};

// ─── Status Config (inline fallback objects to avoid bracket indexing issues) ─

const STATUS_LABEL: Record<WOStatus, string> = {
  draft: 'Draft',
  approved: 'Approved',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  completed: 'Completed',
  invoiced: 'Invoiced',
  cancelled: 'Cancelled',
};

const PRIORITY_LABEL: Record<WOPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

function statusStyle(status: WOStatus): { bg: string; text: string; border: string } {
  const map: Record<WOStatus, { bg: string; text: string; border: string }> = {
    draft:       { bg: 'rgba(156,163,175,0.15)', text: '#9CA3AF', border: 'rgba(156,163,175,0.3)' },
    approved:    { bg: 'rgba(59,130,246,0.15)',  text: '#3B82F6', border: 'rgba(59,130,246,0.3)' },
    assigned:    { bg: 'rgba(6,182,212,0.15)',   text: '#06B6D4', border: 'rgba(6,182,212,0.3)' },
    in_progress: { bg: 'rgba(234,179,8,0.15)',   text: '#EAB308', border: 'rgba(234,179,8,0.3)' },
    completed:   { bg: 'rgba(16,185,129,0.15)',  text: '#10B981', border: 'rgba(16,185,129,0.3)' },
    invoiced:    { bg: 'rgba(168,85,247,0.15)',  text: '#A855F7', border: 'rgba(168,85,247,0.3)' },
    cancelled:   { bg: 'rgba(239,68,68,0.1)',    text: '#6B7280', border: 'rgba(107,114,128,0.2)' },
  };
  return map[status] ?? { bg: 'rgba(156,163,175,0.15)', text: '#9CA3AF', border: 'rgba(156,163,175,0.3)' };
}

function priorityColor(priority: WOPriority): string {
  const map: Record<WOPriority, string> = {
    low:    '#9CA3AF',
    medium: '#3B82F6',
    high:   '#F97316',
    urgent: '#EF4444',
  };
  return map[priority] ?? '#9CA3AF';
}

// ─── Page ──────────────────────────────────────────────────────

export default function WorkOrdersPage() {
  const { data: woData, isLoading, error } = useApi<WorkOrdersResponse>('/api/work-orders');
  const { data: statsData } = useApi<WorkOrderStats>('/api/work-orders/stats');

  const workOrders = woData?.data ?? [];
  const stats = statsData ?? EMPTY_STATS;

  const openCount = (stats.status_counts['draft'] ?? 0) + (stats.status_counts['approved'] ?? 0) + (stats.status_counts['assigned'] ?? 0);
  const inProgressCount = stats.status_counts['in_progress'] ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: 'rgba(16,185,129,0.1)' }}>
            <Wrench className="w-6 h-6" style={{ color: '#10B981' }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Work Orders</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Maintenance work order tracking</p>
          </div>
        </div>
        <button
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
          style={{ background: '#10B981' }}
        >
          <Plus className="w-4 h-4" />
          New Work Order
        </button>
      </div>

      {error && (
        <div className="rounded-xl p-4" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" style={{ color: '#EF4444' }} />
            <span className="text-sm" style={{ color: '#EF4444' }}>Unable to load work orders. Please try again.</span>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <WOStatCard icon={AlertCircle} label="Open" value={String(openCount)} color="#3B82F6" />
        <WOStatCard icon={Clock} label="In Progress" value={String(inProgressCount)} color="#EAB308" />
        <WOStatCard icon={CheckCircle} label="Avg Completion" value={`${stats.avg_completion_days}d`} color="#10B981" />
        <WOStatCard icon={DollarSign} label="Cost This Month" value={`$${stats.total_cost_this_month.toLocaleString()}`} color="#A855F7" />
      </div>

      {/* Table */}
      <div
        className="rounded-xl overflow-hidden"
        style={{
          background: 'var(--glass-bg)',
          border: '1px solid var(--glass-border)',
          backdropFilter: 'blur(var(--glass-blur))',
        }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
          </div>
        ) : workOrders.length === 0 ? (
          <div className="text-center py-16" style={{ color: 'var(--muted-foreground)' }}>
            <Wrench className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No work orders yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--glass-border)' }}>
                  {['WO #', 'Vehicle', 'Description', 'Status', 'Priority', 'Assigned To', 'Cost', 'Created'].map(h => (
                    <th
                      key={h}
                      className="text-left text-xs font-medium uppercase tracking-wide px-4 py-3"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {workOrders.map((wo) => {
                  const ss = statusStyle(wo.status);
                  return (
                    <tr
                      key={wo.id}
                      className="border-b transition-colors hover:bg-white/5"
                      style={{ borderColor: 'var(--glass-border)' }}
                    >
                      {/* WO Number */}
                      <td className="px-4 py-3">
                        <span className="text-sm font-mono font-medium" style={{ color: '#3B82F6' }}>
                          {wo.work_order_number}
                        </span>
                      </td>

                      {/* Vehicle */}
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
                          {wo.vehicle_name}
                        </span>
                      </td>

                      {/* Description */}
                      <td className="px-4 py-3 max-w-xs">
                        <span
                          className="text-sm truncate block"
                          style={{ color: 'var(--muted-foreground)', maxWidth: '200px' }}
                          title={wo.description}
                        >
                          {wo.description}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border"
                          style={{ background: ss.bg, color: ss.text, borderColor: ss.border }}
                        >
                          {STATUS_LABEL[wo.status] ?? wo.status}
                        </span>
                      </td>

                      {/* Priority */}
                      <td className="px-4 py-3">
                        <span
                          className="text-xs font-medium"
                          style={{ color: priorityColor(wo.priority) }}
                        >
                          {PRIORITY_LABEL[wo.priority] ?? wo.priority}
                        </span>
                      </td>

                      {/* Assigned To */}
                      <td className="px-4 py-3">
                        <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                          {wo.assigned_to_name ?? '—'}
                        </span>
                      </td>

                      {/* Cost */}
                      <td className="px-4 py-3">
                        <span className="text-sm font-mono" style={{ color: 'var(--foreground)' }}>
                          {wo.actual_cost != null
                            ? `$${wo.actual_cost.toLocaleString()}`
                            : wo.estimated_cost != null
                              ? `~$${wo.estimated_cost.toLocaleString()}`
                              : '—'}
                        </span>
                      </td>

                      {/* Created */}
                      <td className="px-4 py-3">
                        <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                          {new Date(wo.created_at).toLocaleDateString()}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────

function WOStatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div
      className="rounded-xl p-5"
      style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        backdropFilter: 'blur(var(--glass-blur))',
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
          {label}
        </span>
      </div>
      <span className="text-2xl font-bold" style={{ color }}>{value}</span>
    </div>
  );
}
